import React, { useEffect, useMemo, useState } from "react";
import * as styles from "./SelectedTypeHour.module.scss";
import { ZeusButton, ZeusTypography, ZeusTabMenu } from "zeus-front-design-system";
import typesAttendance from "../../../utils/typesAttendance";
import { useBiometric } from "../../../Context/Biometric/biometric.Context";
import capitalize from "../../../utils/capitalize";
import timeGreeting from "../../../utils/timeGreeting";
import AttendanceShell from "../../../componets/AttendanceShell/AttendanceShell";
import formatName from "../../../utils/formatName";
import { MessageIdColaborator } from "../ControlAttendanceCard/ControlAttendanceCard";
import sortScheduleByPriority from "../../../Helpers/sortScheduleByPriority";
import { store } from "../../../app/store";
import { openConfirm } from "../../../features/modal/modalSlice";
import { useIdleTimer } from "../../../hook/useIdleTimer";
import { useAuth } from "../../../core/auth";
import { clearTheme } from "../../../core/ColorProvider";


type Asistencia = {
  tipo_registro: number;
  fecha_registro?: string;
};

type AllowedType = "entrada" | "salida-comer" | "termino-comida" | "salida";

type TurnStatus = "pending" | "in-progress" | "completed";

export type TurnSchedule = {
  nombre_turno: string;
  id_turno: string | number;
  hora_entrada?: string;
  hora_salida?: string;
  hora_comida_inicio?: string;
  hora_comida_fin?: string;
  asistencias?: Asistencia[];
  estatus: 1 | 2 | 3;
};

interface SelectedTypeHourProps {
  name?: string;
  onSelect: (type: AllowedType, turnoId: string | number) => void;
  schedules: TurnSchedule[];
  returnToIdle: () => void;
}

function getLastAssistanceOfDay(asistencias: Asistencia[] = []): Asistencia | null {
  if (!asistencias.length) return null;
  const sorted = [...asistencias].sort((a, b) => {
    const ta = Date.parse(a.fecha_registro ?? "") || 0;
    const tb = Date.parse(b.fecha_registro ?? "") || 0;
    return tb - ta;
  });
  return sorted[0] ?? null;
}

function mapTipoRegistroToType(n?: number): AllowedType | null {
  switch (n) {
    case 1:
      return "entrada";
    case 2:
      return "salida-comer";
    case 3:
      return "termino-comida";
    case 4:
      return "salida";
    default:
      return null;
  }
}

function deriveAllowedTypes(asistencias?: Asistencia[] | null): Set<AllowedType> {
  const last = getLastAssistanceOfDay(asistencias ?? []);
  const lastType = mapTipoRegistroToType(last?.tipo_registro);

  if (!lastType || lastType === "salida") return new Set<AllowedType>(["entrada"]);
  if (lastType === "entrada") return new Set<AllowedType>(["salida-comer", "salida"]);
  if (lastType === "salida-comer") return new Set<AllowedType>(["termino-comida"]);
  if (lastType === "termino-comida") return new Set<AllowedType>(["salida"]);
  return new Set<AllowedType>(["entrada"]);
}

function getTurnStatusByCode(code: 1 | 2 | 3): TurnStatus {
  switch (code) {
    case 3:
      return "completed";
    case 2:
      return "in-progress";
    default:
      return "pending";
  }
}

function truncateTurnName(name: string, max = 13): string {
  if (name.length <= max) return name;
  return name.slice(0, max) + "…";
}

const MessageWithValidationPhoto: React.FC = () => (
  <ZeusTypography type="Body" size="small" style="bold" component="p" className={styles.helpText}>
    Para completar el registro de cualquier horario, siempre es necesario tomar una fotografía.
  </ZeusTypography>
);

const MessageAttendanceDisabled: React.FC = () => (
  <ZeusTypography
    type="Body"
    size="small"
    style="bold"
    component="p"
    className={styles.helpTextAttendanceDisabled}
  >
    Registra tu hora de salida para poder iniciar tu nuevo turno.
  </ZeusTypography>
);

const MessageAttendanceCompleted: React.FC = () => (
  <ZeusTypography
    type="Body"
    size="small"
    style="bold"
    component="p"
    className={styles.helpTextAttendanceDisabled}
  >
    Tu turno ha sido completado.
  </ZeusTypography>
);

function formatHour(dateStr?: string): string {
  if (!dateStr) return "--:-- h.";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "--:-- h.";
  return (
    d.toLocaleTimeString("es-MX", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }) + " h."
  );
}

function getSummaryFromAsistencias(asistencias: Asistencia[] = []) {
  const summary: {
    entrada?: string;
    salida?: string;
    inicioComida?: string;
    finComida?: string;
  } = {};

  asistencias.forEach((a) => {
    const type = mapTipoRegistroToType(a.tipo_registro);
    switch (type) {
      case "entrada":
        if (!summary.entrada) summary.entrada = formatHour(a.fecha_registro);
        break;
      case "salida":
        summary.salida = formatHour(a.fecha_registro);
        break;
      case "salida-comer":
        summary.inicioComida = formatHour(a.fecha_registro);
        break;
      case "termino-comida":
        summary.finComida = formatHour(a.fecha_registro);
        break;
    }
  });

  return summary;
}

// function filterAsistenciasToday(asistencias: Asistencia[] = []): Asistencia[] {
//   const today = new Date();
//   return asistencias.filter((a) => {
//     if (!a.fecha_registro) return false;
//     const d = new Date(a.fecha_registro);
//     return (
//       d.getFullYear() === today.getFullYear() &&
//       d.getMonth() === today.getMonth() &&
//       d.getDate() === today.getDate()
//     );
//   });
// }

const SelectedTypeHour: React.FC<SelectedTypeHourProps> = ({
  name,
  onSelect,
  schedules,
  returnToIdle,
}) => {
  const { logout } = useAuth();
  const { biometrics } = useBiometric();
  const [helpNode, setHelpNode] = useState<React.ReactNode>(null);
  const [now, setNow] = useState(new Date());
  const [activeTabId, setActiveTabId] = useState<string | number | null>(null);

  // useEffect(() => {
  //   let help: React.ReactNode = null;
  //   if (biometrics?.take_photo) {
  //     help = <MessageWithValidationPhoto />;
  //   } else {
  //     help = <MessageIdColaborator />;
  //   }
  //   setHelpNode(help);
  //   return () => setHelpNode(null);
  // }, [biometrics]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!schedules?.length || activeTabId != null) return;

    const sorted = sortScheduleByPriority(schedules);
    const first = sorted[0];

    if (first) {
      setActiveTabId(first.id_turno);
    }
  }, [schedules, activeTabId, setActiveTabId]);

  const selectedTurn = schedules.find((t) => t.id_turno === activeTabId) ?? schedules[0];

  const asistenciasTurno: Asistencia[] = selectedTurn?.asistencias ?? [];

  const allowed = deriveAllowedTypes(asistenciasTurno);

  const turnsWithStatus = (schedules || []).map((turn) => ({
    ...turn,
    status: getTurnStatusByCode(turn?.estatus ?? 1),
  }));

  const anyInProgress = turnsWithStatus.some((t) => t.status === "in-progress");
  const turnInProgress = turnsWithStatus.find((t) => t.status === "in-progress");

  const sortedSchedules = sortScheduleByPriority(schedules || []);

  const tabList = sortedSchedules.map((turn) => ({
    id: turn.id_turno,
    title: truncateTurnName(turn.nombre_turno),
  }));

  const header = (
    <ZeusTypography
      component="h1"
      type="Headline"
      style="bold"
      size="medium"
      color="#262626"
      className={styles.greeting}
    >
      ¡{capitalize(timeGreeting(now))} {name ? formatName(name) : "colaborador"}!👋
    </ZeusTypography>
  );

  const showFoodForTurn = !!selectedTurn?.hora_comida_inicio || !!selectedTurn?.hora_comida_fin;

  // const foodType: AllowedType | null = allowed.has("salida-comer")
  //   ? "salida-comer"
  //   : allowed.has("termino-comida")
  //     ? "termino-comida"
  //     : "salida-comer";

  // const foodLabel =
  //   foodType === "salida-comer"
  //     ? "Inicio de comida"
  //     : foodType === "termino-comida"
  //       ? "Fin de comida"
  //       : "Inicio de comida";}

  const foodType: AllowedType = allowed.has("termino-comida") ? "termino-comida" : "salida-comer";

  const foodLabel = foodType === "termino-comida" ? "Fin de comida" : "Inicio de comida";

  const isSelectedTurnCompleted = selectedTurn?.estatus === 3;

  const summary = isSelectedTurnCompleted ? getSummaryFromAsistencias(asistenciasTurno) : null;

  const handleTabClick = (id: string | number) => {
    setActiveTabId(id);
  };

  // function isTabDisabled(
  //   tabTurnStatus: TurnStatus,
  //   tabId: string | number,
  //   activeTurnId?: string | number | null
  // ) {
  //   if (tabTurnStatus === "completed") return false;

  //   if (!activeTurnId) return false;

  //   return tabId !== activeTurnId;
  // }

  useEffect(() => {
    const timeout = setTimeout(() => {
      const items = document.querySelectorAll('li[class*="_ZeusTab__List__Item"]');
      items.forEach((li, index) => {
        const fullName = schedules[index]?.nombre_turno;
        if (fullName) {
          li.setAttribute("title", fullName);
        }
      });
    }, 0);

    return () => clearTimeout(timeout);
  }, [schedules, activeTabId]);

  useEffect(() => {
    if (activeTabId == null) return;

    const tabEl = document.getElementById(`zeus-tab-menu-id-${activeTabId}`);
    if (!tabEl) return;

    const wrapper = tabEl.closest("ul");
    if (!wrapper) return;

    tabEl.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "start",
    });
  }, [activeTabId]);

  // useEffect(() => {
  //   if (schedules?.length <= 1) return;
  //   const turnsWithStatus = schedules.map((turn) => ({
  //     id: turn.id_turno,
  //     status: getTurnStatusByCode(turn.estatus ?? 1),
  //   }));

  //   turnsWithStatus.forEach((turn) => {
  //     const tabEl = document.getElementById(`zeus-tab-menu-id-${turn.id}`);
  //     if (!tabEl) return;

  //     const disabled = isTabDisabled(turn.status, turn.id, turnInProgress?.id_turno);

  //     if (disabled) {
  //       tabEl.classList.add("disabled-tab");
  //       tabEl.setAttribute("aria-disabled", "true");
  //       tabEl.style.pointerEvents = "none";
  //     } else {
  //       tabEl.classList.remove("disabled-tab");
  //       tabEl.removeAttribute("aria-disabled");
  //       tabEl.style.pointerEvents = "auto";
  //     }
  //   });
  // }, [schedules, turnInProgress, activeTabId]);

  useEffect(() => {
    if (!isSelectedTurnCompleted && anyInProgress && turnInProgress?.id_turno !== activeTabId) {
      setHelpNode(<MessageAttendanceDisabled />);
    } else if (isSelectedTurnCompleted) {
      setHelpNode(<MessageAttendanceCompleted />);
    } else if (biometrics?.take_photo) {
      setHelpNode(<MessageWithValidationPhoto />);
    } else {
      setHelpNode(null);
    }

    return () => {
      setHelpNode(null);
    };
  }, [
    isSelectedTurnCompleted,
    anyInProgress,
    Boolean(turnInProgress),
    turnInProgress?.id_turno,
    schedules.length,
    biometrics?.take_photo,
    activeTabId,
  ]);

  const timeToMinutes = (time: string): number => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };
  

  const handleClockOut = (
    salidaType: AllowedType,
    scheduledExitTime?: string
  ) => {
    // ⬅️ Si no hay hora programada, ejecuta directo
    if (!scheduledExitTime) {
      onSelect(salidaType, selectedTurn.id_turno);
      return;
    }
  
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const exitMinutes = timeToMinutes(scheduledExitTime);
  
    // ⬅️ Antes de la hora de salida → confirmación
    if (currentMinutes < exitMinutes) {
      store.dispatch(
        openConfirm({
          title: "¿100% Seguro?",
          subtitle:
            "Tu turno aún no termina, al continuar esta acción registrará el final de tu jornada del día de hoy",
          confirmText: "Continuar",
          onAccept: () => {
            onSelect(salidaType, selectedTurn.id_turno);
          },
        })
      );
      return;
    }
  
    // ⬅️ Igual o después → directo
    onSelect(salidaType, selectedTurn.id_turno);
  };

  useIdleTimer(() => {
    clearTheme();
    returnToIdle();
  });
  
  

  return (
    <AttendanceShell
      header={header}
      subtitle="Realiza el registro de hoy."
      helpNode={helpNode}
      timeFormat="24h"
      showBackButton
      onBack={returnToIdle}
      dividerText="¿Qué horario quieres registrar?"
    >
      {activeTabId != null && tabList.length > 0 && (
        <div className={styles.tabsWrapper}>
          <ZeusTabMenu
            tabList={tabList}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            className={styles.tabsContainer}
          />
        </div>
      )}

      {selectedTurn && selectedTurn.nombre_turno.length > 13 && (
        <ZeusTypography type="Body" size="small" component="p" className={styles.turnFullName}>
          {selectedTurn.nombre_turno}
        </ZeusTypography>
      )}

      {isSelectedTurnCompleted && (
        <>
          <div className={`${styles.summaryGrid} ${!showFoodForTurn ? styles.withoutFood : ""}`}>
            <div className={styles.summaryCard}>
              <ZeusTypography
                type="Body"
                size="medium"
                style="bold"
                component="p"
                className={styles.summaryCardTitle}
              >
                Asistencia
              </ZeusTypography>

              <div className={`${styles.summaryRow} `}>
                <ZeusTypography type="Body" size="small" component="span">
                  Entrada
                </ZeusTypography>
                <ZeusTypography
                  className={styles.hour}
                  type="Body"
                  size="large"
                  style="bold"
                  component="span"
                >
                  {summary?.entrada ?? "--:-- h."}
                </ZeusTypography>
              </div>

              <div className={`${styles.summaryRow}`}>
                <ZeusTypography type="Body" size="small" component="span">
                  Salida
                </ZeusTypography>
                <ZeusTypography
                  type="Body"
                  size="large"
                  style="bold"
                  component="span"
                  className={styles.hour}
                >
                  {summary?.salida ?? "--:-- h."}
                </ZeusTypography>
              </div>
            </div>

            {showFoodForTurn && (
              <div className={styles.summaryCard}>
                <ZeusTypography
                  type="Body"
                  size="medium"
                  style="bold"
                  component="p"
                  className={styles.summaryCardTitle}
                >
                  Comida
                </ZeusTypography>

                <div className={`${styles.summaryRow} ${styles.alignItems}`}>
                  <ZeusTypography type="Body" size="small" component="span">
                    Inicio de comida
                  </ZeusTypography>
                  <ZeusTypography
                    type="Body"
                    size="large"
                    style="bold"
                    component="span"
                    className={styles.hour}
                  >
                    {summary?.inicioComida ?? "--:-- h."}
                  </ZeusTypography>
                </div>

                {summary?.finComida && (
                  <div className={`${styles.summaryRow} ${styles.alignItems}`}>
                    <ZeusTypography type="Body" size="small" component="span">
                      Fin de comida
                    </ZeusTypography>
                    <ZeusTypography
                      type="Body"
                      size="large"
                      style="bold"
                      component="span"
                      className={styles.hour}
                    >
                      {summary?.finComida ?? "--:-- h."}
                    </ZeusTypography>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <div className={styles.buttonsContainer}>
        {!isSelectedTurnCompleted && (
          <div className={styles.buttonsContainer}>
            {(() => {
              const entradaType = typesAttendance()[1] as AllowedType;
              const isAllowed = allowed.has("entrada");
              const disabledByOtherTurn =
                anyInProgress &&
                turnInProgress &&
                selectedTurn?.id_turno !== turnInProgress.id_turno;
              // if (disabledByOtherTurn) setHelpNode(<MessageAttendanceDisabled />);

              const isDisabled = !isAllowed || disabledByOtherTurn;

              return (
                <ZeusButton
                  className={styles.button}
                  textContent="Entrada"
                  withArrow={false}
                  width="100%"
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  onClick={() => !isDisabled && onSelect(entradaType, selectedTurn.id_turno)}
                />
              );
            })()}

            {showFoodForTurn && (
              <ZeusButton
                className={styles.button}
                textContent={foodLabel}
                withArrow={false}
                width="100%"
                disabled={
                  !allowed.has(foodType) ||
                  (anyInProgress &&
                    turnInProgress &&
                    selectedTurn?.id_turno !== turnInProgress.id_turno)
                }
                aria-disabled={
                  !allowed.has(foodType) ||
                  (anyInProgress &&
                    turnInProgress &&
                    selectedTurn?.id_turno !== turnInProgress.id_turno)
                }
                onClick={() => onSelect(foodType, selectedTurn.id_turno)}
              />
            )}

            {(() => {
              const salidaType = typesAttendance()[4] as AllowedType;
              const isAllowed = allowed.has("salida");
              const disabledByOtherTurn =
                anyInProgress &&
                turnInProgress &&
                selectedTurn?.id_turno !== turnInProgress.id_turno;

              const isDisabled = !isAllowed || disabledByOtherTurn;

              return (
                <ZeusButton
                  className={styles.button}
                  textContent="Salida"
                  withArrow={false}
                  width="100%"
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  onClick={() => !isDisabled && handleClockOut(salidaType, selectedTurn.hora_salida)}
                />
              );
            })()}
          </div>
        )}
      </div>
    </AttendanceShell>
  );
};

export default SelectedTypeHour;
